import { ArrowUpRight, ChevronDown, ChevronUp } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import IconAccountActive from '@/assets/smartcity/account-active.svg';
import IconAccountInactive from '@/assets/smartcity/account-inactive.svg';
import IconGeneralActive from '@/assets/smartcity/general-active.svg';
import IconGeneralInActive from '@/assets/smartcity/general-inactive.svg';
import IconPrivacyActive from '@/assets/smartcity/privacy-active.svg';
import IconPrivacyInactive from '@/assets/smartcity/privacy-inactive.svg';

type Category = 'General' | 'Account' | 'Privacy & Security';

interface CategoryItem {
  name: Category;
  imgActive: string;
  imgInactive: string;
}

interface FaqItem {
  question: string;
  answer: React.ReactNode;
}

const faqCategories: CategoryItem[] = [
  {
    name: 'General',
    imgActive: IconGeneralActive,
    imgInactive: IconGeneralInActive,
  },

  {
    name: 'Account',
    imgActive: IconAccountActive,
    imgInactive: IconAccountInactive,
  },
  {
    name: 'Privacy & Security',
    imgActive: IconPrivacyActive,
    imgInactive: IconPrivacyInactive,
  },
];

const faqData: Record<Category, FaqItem[]> = {
  General: [
    {
      question: 'Who can use the PSC Mobile App?',
      answer:
        'The application is intended for authorized ambulance personnel registered by the Public Safety Center.',
    },
    {
      question: 'What is the purpose of the PSC Mobile App?',
      answer:
        'The app helps ambulance teams receive dispatches, manage emergency missions, update operational status, and complete digital reports.',
    },
    {
      question: 'Does the application work without an internet connection?',
      answer:
        'An internet connection is required to receive real-time assignments, synchronize data, and communicate with the dispatch center.',
    },
    {
      question: 'Can I access my previous assignments?',
      answer:
        'Yes. All ongoing and completed assignments are available in the History section for future reference.',
    },
  ],

  Account: [
    {
      question: 'How do I log in to the application?',
      answer: 'Log in using the account credentials provided by your PSC administrator.',
    },
    {
      question: 'What should I do if I forget my password?',
      answer: 'Contact your PSC administrator to reset your account password.',
    },
    {
      question: 'Can I update my profile information?',
      answer:
        'Yes. You can update your profile information through the Profile section, depending on your account permissions.',
    },
    {
      question: 'Can I use my account on multiple devices?',
      answer:
        'Yes. Your account is assigned to a specific healthcare facility. However, if multiple vehicles are registered under that healthcare facility, you can switch between them from your account.',
    },
  ],

  'Privacy & Security': [
    {
      question: 'Is my personal data secure?',
      answer:
        'Yes. Your personal and operational data is protected using secure authentication and encrypted communication.',
    },
    {
      question: 'Why does the application require location access?',
      answer:
        'Location access is used only during emergency operations to support navigation, live tracking, and dispatch coordination.',
    },
    {
      question: 'Is my operational activity recorded?',
      answer:
        'Yes. Mission updates, status changes, and reports are automatically logged to ensure operational transparency and accountability.',
    },
  ],
};

const FaqDropdown: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<Category>('General');

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="flex w-full gap-5 lg:flex-row md:flex-row flex-col">
      {/* Left */}
      <div className="lg:w-[20%] md:w-[25%] w-full">
        <ul className="flex flex-col gap-3">
          {faqCategories.map((cat) => (
            <li key={cat.name}>
              <button
                type="button"
                onClick={() => {
                  setActiveCategory(cat.name);
                  setOpenIndex(0);
                }}
                className={`w-full rounded-xl p-3 flex items-center gap-3 transition-all ${
                  activeCategory === cat.name
                    ? 'bg-text-blue-100 text-white'
                    : 'hover:bg-[#E9EFEA] text-text-grey'
                }`}
              >
                <img
                  src={activeCategory === cat.name ? cat.imgActive : cat.imgInactive}
                  alt={cat.name}
                  className="h-6 w-6"
                />

                {cat.name}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Right */}
      <div className="flex-1">
        <div className="flex flex-col gap-2">
          {faqData[activeCategory].map((item, index) => (
            <div
              key={item.question}
              className={`overflow-hidden rounded-xl ${
                openIndex === index ? 'bg-text-blue-100 text-white' : 'bg-gray-50'
              }`}
            >
              <button
                type="button"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className={`flex w-full items-center justify-between p-6 text-left transition-colors ${
                  openIndex === index ? 'bg-text-blue-100' : 'hover:bg-gray-100'
                }`}
              >
                <span className="font-semibold">{item.question}</span>

                {openIndex === index ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
              </button>

              {openIndex === index && (
                <div className="px-6 pb-6 leading-relaxed">{item.answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FaqDropdown;
